// src/pages/Contact.js
import React, { useState } from "react";
import "../index.css";
import useSEO from "../seo/useSEO";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";

export default function Contact() {
  // ✅ SEO pour la page Contact
  useSEO({
    title: "Contact — Maxime H, photographe culinaire",
    description:
      "Contactez-moi pour un devis : réponse sous 24h. Déplacements France, Belgique, Luxembourg.",
    canonical: `${window.location.origin}/contact`,
    image: "/images/og-default.jpg",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "Maxime H — Photographe culinaire",
      url: "https://www.maximeh.fr",
      image: "https://www.maximeh.fr/images/og-default.jpg",
      telephone: "+33663252202",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Tomblaine",
        postalCode: "54510",
        addressCountry: "FR",
      },
      areaServed: ["FR", "BE", "LU"],
      sameAs: [
        "https://www.instagram.com/maximehphoto",
        "https://www.linkedin.com/in/maximehculinaire/",
        "https://www.facebook.com/maximehculinaire",
      ],
    },
  });

  const [form, setForm] = useState({
    nom: "",
    email: "",
    tel: "",
    sujet: "Demande de devis",
    message: "",
  });
  const [copied, setCopied] = useState(null);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopied("error");
      setTimeout(() => setCopied(null), 1500);
    }
  };

  const buildMailto = () => {
    const to = "contact@maximeh.fr";
    const subject = `${form.sujet} – ${form.nom || "Contact site"}`;
    const body = [
      `Bonjour Maxime,`,
      ``,
      `Nom : ${form.nom || ""}`,
      `Email : ${form.email || ""}`,
      `Téléphone : ${form.tel || ""}`,
      ``,
      `Sujet : ${form.sujet}`,
      ``,
      `${form.message || ""}`,
      ``,
      `--`,
      `Envoyé depuis le site maximeh.fr`,
    ].join("\n");

    return `mailto:${to}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <section className="contact-page">
      <div className="contact-grid-areas">
        {/* A — Portrait (gauche) */}
        <div className="contact-card portrait-area">
          <img
            src="/images/portrait.jpg"
            alt="Portrait de Maxime Huylbroeck"
            className="contact-portrait-img"
            loading="lazy"
          />
        </div>

        {/* B — Coordonnées + Infos pratiques (droite) */}
        <div className="contact-card coords-area">
          <h2>Coordonnées</h2>

          <div className="contact-rows">
            <div className="row">
              <span className="row-label">Email</span>
              <div className="row-val">
                <a href="mailto:contact@maximeh.fr">contact@maximeh.fr</a>
                <button
                  className="mini"
                  onClick={() => copy("contact@maximeh.fr", "email")}
                  aria-label="Copier l'email"
                >
                  {copied === "email" ? "Copié" : "Copier"}
                </button>
              </div>
            </div>

            <div className="row">
              <span className="row-label">Téléphone</span>
              <div className="row-val">
                <a href="tel:+33663252202">+33 6 63 25 22 02</a>
                <button
                  className="mini"
                  onClick={() => copy("+33663252202", "tel")}
                  aria-label="Copier le téléphone"
                >
                  {copied === "tel" ? "Copié" : "Copier"}
                </button>
              </div>
            </div>

            <div className="row">
              <span className="row-label">Adresse</span>
              <div className="row-val">Tomblaine (54510), France</div>
            </div>
          </div>

          <div className="badges">
            <span className="badge">🇫🇷</span>
            <span className="badge">🇧🇪</span>
            <span className="badge">🇱🇺</span>
            <span className="badge">Réponse &lt; 24h</span>
            <span className="badge">Déplacement sur site</span>
          </div>

          {/* Infos pratiques + liens */}
          <div className="infos-pratiques">
            <h3>Infos pratiques</h3>
            <ul>
              <li>
                📄 Devis sous 24h · Prise de vue possible sous 7–10 jours selon
                planning.
              </li>
              <li>
                🚗 Déplacements France, Belgique, Luxembourg — sur devis selon
                distance.
              </li>
              <li>
                🖼️ Livrables optimisés web & print, galerie de sélection en
                ligne incluse.
              </li>
              <li>
                🌟 Photographe officiel{" "}
                <strong>Gault&Millau Luxembourg</strong>.
              </li>
            </ul>

            <div className="socials">
              <a
                href="https://www.facebook.com/maximehculinaire"
                target="_blank"
                rel="noreferrer"
              >
                <FaFacebookF size={22} />
              </a>
              <a
                href="https://www.instagram.com/maximehphoto"
                target="_blank"
                rel="noreferrer"
              >
                <FaInstagram size={22} />
              </a>
              <a
                href="https://www.linkedin.com/in/maximehculinaire/"
                target="_blank"
                rel="noreferrer"
              >
                <FaLinkedinIn size={22} />
              </a>
            </div>
          </div>
        </div>

        {/* C — Écrivez-moi (pleine largeur) */}
        <div className="contact-card form-area">
          <h2>Écrivez-moi</h2>
          <form
            className="contact-form"
            onSubmit={(e) => {
              e.preventDefault();
              window.location.href = buildMailto();
            }}
          >
            <div className="grid">
              <div className="field">
                <label htmlFor="nom">Nom *</label>
                <input
                  id="nom"
                  name="nom"
                  type="text"
                  required
                  value={form.nom}
                  onChange={update}
                  placeholder="Votre nom / société"
                />
              </div>
              <div className="field">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update}
                  placeholder="vous@exemple.com"
                />
              </div>
              <div className="field">
                <label htmlFor="tel">Téléphone</label>
                <input
                  id="tel"
                  name="tel"
                  type="tel"
                  value={form.tel}
                  onChange={update}
                  placeholder="+33 ..."
                />
              </div>
              <div className="field">
                <label htmlFor="sujet">Sujet *</label>
                <select
                  id="sujet"
                  name="sujet"
                  value={form.sujet}
                  onChange={update}
                >
                  <option>Demande de devis</option>
                  <option>Informations</option>
                  <option>Collaboration</option>
                  <option>Autre</option>
                </select>
              </div>
            </div>

            <div className="field">
              <label htmlFor="message">Votre message *</label>
              <textarea
                id="message"
                name="message"
                rows="6"
                required
                value={form.message}
                onChange={update}
                placeholder="Parlez-moi de votre projet, dates, lieu, usage des images…"
              />
            </div>

            <div className="actions">
              <button type="submit" className="btn-solid">
                Envoyer
              </button>
            </div>

            <p className="hint">
              En envoyant ce message, vous acceptez d’être recontacté par email
              ou téléphone pour votre demande.
            </p>
          </form>
        </div>

        {/* D — Plan (pleine largeur) */}
        <div className="contact-card map-area">
          <iframe
            title="Carte Tomblaine"
            src="https://www.google.com/maps?q=Tomblaine%2054510&output=embed"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="contact-map-iframe"
          ></iframe>
        </div>
      </div>
    </section>
  );
}